
import { useState, useCallback, useEffect } from "react";
import { Article } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useArticleLoader = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadTime, setLastLoadTime] = useState<Date | null>(null);
  const { toast } = useToast();

  const loadArticles = useCallback(async (retryCount = 0, maxRetries = 3, showToastOnError = true): Promise<void> => {
    if (loading) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log("Fetching articles from Supabase...");
      
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          author:author_id (
            id,
            first_name,
            last_name,
            bio,
            profile_image_url,
            created_at,
            updated_at
          ),
          tags:article_tags_map (
            tag:tag_id (
              id,
              name,
              slug
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log("Articles data received:", data?.length || 0, "articles");

      // Safely handle tag mapping with proper null checks
      const transformedArticles = data.map((article: any) => ({
        ...article,
        tags: (article.tags || [])
          .filter((tagItem: any) => tagItem && tagItem.tag)
          .map((tagItem: any) => tagItem.tag)
      }));

      setArticles(transformedArticles);
      setLastLoadTime(new Date());
    } catch (error: any) {
      console.error('Error loading articles:', error);
      setError(error.message || "Unknown error loading articles");
      
      // Retry logic for network errors
      if (retryCount < maxRetries && 
          (error.message.includes('Failed to fetch') || 
           error.code === 'ERR_INSUFFICIENT_RESOURCES')) {
        console.log(`Retrying loadArticles (${retryCount + 1}/${maxRetries})...`);
        setTimeout(() => {
          loadArticles(retryCount + 1, maxRetries, showToastOnError);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
        return;
      }
      
      if (showToastOnError) {
        toast({
          variant: "destructive",
          title: "Error loading articles",
          description: error.message || "Failed to load articles"
        });
      }
    } finally {
      setLoading(false);
    }
  }, [loading, toast]);

  // Add a function to force reload articles even if they're already loaded
  const forceReloadArticles = useCallback(async () => {
    console.log("Force reloading articles...");
    await loadArticles(0, 3);
  }, [loadArticles]);

  return {
    articles,
    loading,
    error,
    lastLoadTime,
    setArticles,
    loadArticles,
    forceReloadArticles
  };
};
